import { LangCode } from "@/lib/types";
import { VALID_LANGS } from "./locale";
import { COMPANY } from "@/lib/company";
import { activeDict } from "@/lib/i18n-dict";

export interface PageMeta {
  title: string;
  description: string;
}

export type SeoPage =
  | "home"
  | "about"
  | "products"
  | "blog"
  | "services"
  | "projects"
  | "seafood"
  | "seafoodEurope"
  | "contact"
  | "quote"
  | "faq"
  | "salmonOva"
  | "oliveFlounderExport"
  | "expoFutureFish"
  | "artemiaGuide"
  | "decapGuide"
  | "glossary"
  | "horeca"
  | "regionalTrade"
  | "aquariums"
  | "privacy"
  | "terms"
  | "login"
  | "register"
  | "account"
  | "cart"
  | "shop"
  | "checkout";

/** Company identity for locale-sensitive metadata and structured data. */
export function localizedCompanyName(lang: LangCode = getCurrentLocaleForSeo()): string {
  return lang === "tr" ? COMPANY.shortName : COMPANY.tradeName;
}

/** Preserve existing non-Turkish branding while Turkish titles use compact VARS. */
export function localizedBrandTitle(
  title: string,
  lang: LangCode = getCurrentLocaleForSeo(),
): string {
  return `${title} | ${lang === "tr" ? COMPANY.compactName : COMPANY.tradeName}`;
}

/*
 * Search engines do not use meta keywords as a ranking signal. These page
 * descriptions intentionally keep the site's established commercial terms
 * (live feed, Artemia, salmon eggs, seafood export, B2B) in natural copy.
 * Keeping this data here makes SSR metadata and all locale variants auditable.
 */
const SEO_COPY: Partial<Record<SeoPage, Record<LangCode, PageMeta>>> = {
  /**
   * Aquariums and hobbyists.
   *
   * This entry exists because the page had none. Without one it fell through to
   * localizedGeneric(), which serves the ENGLISH title and description to all
   * eight non-English locales with " | VARS Aquaculture" appended — so
   * /tr/aquariums-and-hobbyists and /ja/aquariums-and-hobbyists were competing
   * for their markets with English metadata.
   *
   * The wording changed at the same time. Search Console for 2026-08-03 records
   * no impressions at all for this URL, while the retired Odoo category
   * /shop/category/aquarium-ornamental-feed-7 still ranks at position 2 — the
   * demand is real and was landing somewhere else. The old title named only
   * "Aquariums, Retailers & Hobbyists"; the page now covers goldfish, koi and
   * ornamental fry, so the metadata says so. Nothing was lost by rewriting it,
   * because there was nothing there to lose.
   */
  aquariums: {
    en: {
      title: "Live Feed for Aquariums, Goldfish & Ornamental Fry | VARS",
      description:
        "Artemia cysts, shell-free decapsulated Artemia and Chlorella microalgae for goldfish, koi, betta and marine fry — for aquarium shops, breeders and public aquariums.",
    },
    tr: {
      title: "Akvaryum ve Süs Balıkları İçin Canlı Yem | VARS",
      description:
        "Japon balığı, koi, beta ve deniz balığı yavruları için Artemia yumurtası, dekapsüle Artemia ve Chlorella ürünlerini inceleyin.",
    },
    de: {
      title: "Lebendfutter für Aquarien und Zierfischbrut | VARS",
      description:
        "Artemia-Zysten, entkapselte Artemia und Chlorella-Mikroalgen für Zierfischbrut — für Zoofachhandel, Züchter und Schauaquarien.",
    },
    ar: {
      title: "أغذية حية لأحواض السمك ويرقات الزينة | VARS",
      description:
        "حويصلات الأرتيميا والأرتيميا المقشّرة والكلوريلا لصغار السمك الذهبي والكوي والبيتا وأسماك البحر — لمتاجر الأحواض والمربّين.",
    },
    ru: {
      title: "Живые корма для аквариумов и декоративных рыб | VARS",
      description:
        "Цисты артемии, декапсулированная артемия и хлорелла для мальков декоративных рыб — для зоомагазинов и океанариумов.",
    },
    ja: {
      title: "アクアリウム・観賞魚稚魚のための生餌 | VARS",
      description:
        "金魚、錦鯉、ベタ、海水魚の稚魚に適したアルテミア耐久卵、脱殻アルテミア、クロレラ微細藻類。ショップ、水族館向け。",
    },
    ko: {
      title: "관상어·금붕어 치어를 위한 생사료 | VARS",
      description:
        "금붕어, 잉어, 베타, 해수어 치어를 위한 아르테미아 내구란, 탈각 아르테미아, 클로렐라 미세조류 — 수족관 매장, 브리더용.",
    },
    zh: {
      title: "水族箱、金鱼与观赏鱼苗专用活饵 | VARS",
      description:
        "适用于金鱼、锦鲤、斗鱼及海水鱼苗的丰年虫卵、去壳丰年虫和小球藻微藻——面向水族店、繁殖者与水族馆。",
    },
    es: {
      title: "Alimento vivo para acuarios y alevines ornamentales | VARS",
      description:
        "Quistes de artemia, artemia descapsulada y microalgas de clorela para alevines ornamentales — para tiendas, criadores y acuarios.",
    },
    fr: {
      title: "Proies vivantes pour aquariums et alevins d'ornement | VARS",
      description:
        "Cystes d'artémia, artémia décapsulée sans coque et microalgues Chlorella pour poissons rouges, koïs, combattants et alevins marins — pour magasins d'aquariophilie, éleveurs et aquariums publics.",
    },
    fa: {
      title: "غذای زنده آکواریوم و بچه‌ماهی زینتی | VARS",
      description:
        "سیست آرتمیا، آرتمیای دکپسوله و ریزجلبک کلرلا برای ماهی قرمز، کوی و لارو دریایی — ویژه فروشگاه‌های آکواریوم و پرورش‌دهندگان.",
    },
  },
  expoFutureFish: {
    en: {
      title: "Meet VARS at Future Fish Eurasia 2026 — Hall C, Stand C-237",
      description:
        "VARS at Future Fish Eurasia 2026 in İzmir, 24–26 Sept, Fuar İzmir Hall C, stand C-237. Interactive hall map, showcased products, and meeting booking.",
    },
    tr: {
      title: "Future Fish Eurasia 2026 | Hol C, Stant C-237 | VARS",
      description:
        "VARS Su Ürünleri, 24–26 Eylül 2026 tarihlerinde Fuar İzmir Hol C, C-237 numaralı stantta. Etkileşimli salon planı ve görüşme randevusu.",
    },
    de: {
      title: "VARS auf der Future Fish Eurasia 2026 — Halle C, Stand C-237",
      description:
        "VARS stellt vom 24.–26. September 2026 auf der Future Fish Eurasia in İzmir aus, Fuar İzmir Halle C, Stand C-237. Interaktiver Hallenplan und Terminvereinbarung.",
    },
    ar: {
      title: "VARS في Future Fish Eurasia 2026 — القاعة C، جناح C-237",
      description:
        "تشارك VARS في معرض Future Fish Eurasia 2026 بإزمير، 24–26 سبتمبر، قاعة Fuar İzmir C، الجناح C-237. خريطة تفاعلية للقاعة وحجز موعد.",
    },
    ru: {
      title: "VARS на Future Fish Eurasia 2026 — зал C, стенд C-237",
      description:
        "VARS участвует в выставке Future Fish Eurasia 2026 в Измире, 24–26 сентября, Fuar İzmir зал C, стенд C-237. Интерактивный план зала и запись на встречу.",
    },
    ja: {
      title: "Future Fish Eurasia 2026 出展案内 | VARS",
      description:
        "VARSは2026年9月24〜26日、イズミルのFuar İzmirホールC、ブースC-237に出展します。インタラクティブな会場マップと商談予約はこちら。",
    },
    ko: {
      title: "Future Fish Eurasia 2026 참가 안내 | VARS",
      description:
        "VARS는 2026년 9월 24~26일 이즈미르 Fuar İzmir C홀 C-237 부스에 참가합니다. 전시장 지도 및 미팅 예약.",
    },
    zh: {
      title: "Future Fish Eurasia 2026 参展指南 | VARS",
      description:
        "VARS 将参展伊兹密尔 Future Fish Eurasia 2026，Fuar İzmir C 馆 C-237 展位。展位地图与会面预约。",
    },
    es: {
      title: "VARS en Future Fish Eurasia 2026 — Pabellón C, Stand C-237",
      description:
        "VARS en Future Fish Eurasia 2026 en Esmirna, 24–26 sept, Fuar İzmir pabellón C, stand C-237. Mapa interactivo del pabellón y reserva de reuniones.",
    },
    fr: {
      title: "Retrouvez VARS à Future Fish Eurasia 2026 — hall C, stand C-237",
      description:
        "VARS à Future Fish Eurasia 2026 à İzmir, du 24 au 26 septembre, Fuar İzmir hall C, stand C-237. Plan interactif du hall, produits exposés et prise de rendez-vous.",
    },
    fa: {
      title: "VARS در Future Fish Eurasia 2026 — سالن C، غرفه C-237",
      description:
        "VARS در نمایشگاه Future Fish Eurasia 2026 ازمیر، ۲۴–۲۶ سپتامبر، سالن C، غرفه C-237 حضور دارد. نقشه سالن و رزرو جلسه.",
    },
  },
  glossary: {
    en: {
      title: "Aquaculture Glossary — Live Feed & Export Terms | VARS",
      description:
        "Definitions of the aquaculture terms buyers meet in practice: Artemia and live feed, hatchery and larval rearing, salmonid ova, biosecurity, feed and seafood export.",
    },
    tr: {
      title: "Su Ürünleri Sözlüğü | VARS",
      description:
        "Canlı yem, kuluçkahane, larva yetiştiriciliği, somon yumurtası, biyogüvenlik, yem ve ihracat terimlerinin açıklamalarını bulun.",
    },
    de: {
      title: "Aquakultur-Glossar — Lebendfutter & Exportbegriffe | VARS",
      description:
        "Aquakulturbegriffe aus der Praxis: Artemia und Lebendfutter, Brüterei und Larvenaufzucht, Salmonideneier, Biosicherheit, Futter und Meeresfrüchteexport.",
    },
    ar: {
      title: "مسرد الاستزراع المائي — الغذاء الحي ومصطلحات التصدير | VARS",
      description:
        "تعريفات مصطلحات الاستزراع المائي: الأرتيميا والغذاء الحي، المفرخات وتربية اليرقات، بيض السلمونيات، الأمن الحيوي، الأعلاف وتصدير المأكولات البحرية.",
    },
    ru: {
      title: "Глоссарий аквакультуры — живые корма и экспорт | VARS",
      description:
        "Термины аквакультуры: артемия и живые корма, инкубаторы и личинки, икра лососевых, биобезопасность, корма и экспорт морепродуктов.",
    },
    ja: {
      title: "養殖用語集 — 生餌・種苗生産・輸出の用語 | VARS",
      description:
        "実務で使われる養殖用語の解説：アルテミアと生餌、種苗生産と仔魚飼育、サケ科発眼卵、バイオセキュリティ、飼料、水産物輸出。",
    },
    ko: {
      title: "양식 용어집 — 생사료·부화장·수출 용어 | VARS",
      description:
        "현장에서 쓰이는 양식 용어 정리: 알테미아와 생사료, 부화장과 자어 사육, 연어과 발안란, 바이오시큐리티, 사료, 수산물 수출.",
    },
    zh: {
      title: "水产养殖术语表 — 活饵、育苗与出口术语 | VARS",
      description:
        "买家在实务中会遇到的水产养殖术语释义：丰年虫与活饵、育苗与幼体培育、鲑鳟发眼卵、生物安全、饲料及水产品出口。",
    },
    es: {
      title: "Glosario de acuicultura — alimento vivo y exportación | VARS",
      description:
        "Términos de acuicultura práctica: Artemia y alimento vivo, criadero y cría larvaria, ovas de salmónidos, bioseguridad, pienso y exportación de pescado.",
    },
    fr: {
      title: "Glossaire de l'aquaculture — proies vivantes et export | VARS",
      description:
        "Définitions des termes aquacoles que les acheteurs rencontrent en pratique : artémia et proies vivantes, écloserie et élevage larvaire, œufs de salmonidés, biosécurité, alimentation et export de produits de la mer.",
    },
    fa: {
      title: "واژه‌نامه آبزی‌پروری — غذای زنده و اصطلاحات صادرات | VARS",
      description:
        "تعریف اصطلاحات آبزی‌پروری: آرتمیا و غذای زنده، هچری و پرورش لارو، تخم ماهیان آزاد، ایمنی زیستی، خوراک و صادرات آبزیان.",
    },
  },
  services: {
    en: {
      title: "Aquaculture Consulting, Sourcing & Export Solutions | VARS",
      description:
        "Technical consulting, hatchery biosecurity, and cold-chain supply chain management for commercial aquaculture projects worldwide.",
    },
    tr: {
      title: "Su Ürünleri Danışmanlığı ve İhracat | VARS",
      description:
        "B2B alıcılar için kuluçkahane ve RAS desteği, yem ve girdi tedariki, sorun giderme ve ihracata hazırlık çözümlerini inceleyin.",
    },
    de: {
      title: "Beratung, Beschaffung & Export in der Aquakultur | VARS",
      description:
        "Technische Beratung, Biosicherheit für Brütereien und Kühlkettenlogistik für kommerzielle Aquakulturprojekte weltweit.",
    },
    ar: {
      title: "استشارات وتوريد وحلول تصدير الاستزراع المائي | VARS",
      description:
        "الاستشارات الفنية، والأمن الحيوي للمفرخات، وإدارة سلسلة التبريد لمشاريع الاستزراع المائي التجارية عالميًا.",
    },
    ru: {
      title: "Консалтинг, снабжение и экспорт в аквакультуре | VARS",
      description:
        "Технический консалтинг, биобезопасность инкубаториев и управление холодовой цепью для коммерческих проектов аквакультуры.",
    },
    ja: {
      title: "養殖コンサルティング・調達・輸出ソリューション | VARS",
      description:
        "世界中の商業養殖プロジェクトに向けた技術コンサルティング、孵化場のバイオセキュリティ、コールドチェーン管理。",
    },
    ko: {
      title: "양식 컨설팅, 소싱 및 수출 솔루션 | VARS",
      description:
        "글로벌 상업 양식 프로젝트를 위한 기술 컨설팅, 부화장 바이오보안, 콜드체인 공급망 관리 솔루션.",
    },
    zh: {
      title: "水产养殖咨询、采购与出口解决方案 | VARS",
      description: "为全球商业水产养殖项目提供技术咨询、育苗场生物安全与冷链供应链管理支持。",
    },
    es: {
      title: "Consultoría, suministro y exportación acuícola | VARS",
      description:
        "Consultoría técnica, bioseguridad en criaderos y gestión de cadena de frío para proyectos acuícolas comerciales.",
    },
    fr: {
      title: "Conseil, sourcing et solutions export en aquaculture | VARS",
      description:
        "Conseil technique, biosécurité d'écloserie et gestion de la chaîne d'approvisionnement sous chaîne du froid pour les projets aquacoles commerciaux dans le monde entier.",
    },
    fa: {
      title: "مشاوره، تأمین و راهکارهای صادرات آبزی‌پروری | VARS",
      description:
        "مشاوره تخصصی آبزی‌پروری، پشتیبانی هچری و سیستم‌های مداربسته، تأمین خوراک، عیب‌یابی و آماده‌سازی صادرات برای خریداران بین‌المللی.",
    },
  },
  projects: {
    en: {
      title: "Aquaculture Projects & Supply Chain Case Studies | VARS",
      description:
        "Review VARS aquaculture projects, hatchery trials, salmon egg exports, live feed optimization, and international supply-chain results.",
    },
    tr: {
      title: "Su Ürünleri Projeleri ve Vaka Çalışmaları | VARS",
      description:
        "Su ürünleri yetiştiriciliği, tedarik, lojistik ve üretim desteği alanlarındaki VARS proje ve uygulama örneklerini inceleyin.",
    },
    de: {
      title: "Aquakulturprojekte und Lieferketten-Fallstudien | VARS",
      description:
        "Entdecken Sie VARS-Projekte, Brüterei-Versuche, Lachseierexporte, Lebendfutteroptimierung und internationale Lieferketten.",
    },
    ar: {
      title: "مشاريع الاستزراع المائي ودراسات سلسلة التوريد | VARS",
      description:
        "اطلع على مشاريع VARS وتجارب المفرخات وتصدير بيض السلمون وتحسين الأعلاف الحية ونتائج سلاسل التوريد الدولية.",
    },
    ru: {
      title: "Проекты в аквакультуре и кейсы цепочки поставок | VARS",
      description:
        "Изучите проекты VARS, испытания на инкубаторах, экспорт икры лосося, оптимизацию живых кормов и международную логистику.",
    },
    ja: {
      title: "養殖プロジェクトとサプライチェーン事例 | VARS",
      description:
        "VARSの養殖プロジェクト、ふ化場試験、サーモン卵輸出、生餌の最適化、国際物流の成果を紹介します。",
    },
    ko: {
      title: "양식 프로젝트 및 공급망 사례 연구 | VARS",
      description:
        "VARS의 양식 프로젝트, 부화장 시험, 연어 수정란 수출, 생먹이 최적화 및 국제 공급망 성과를 확인하세요.",
    },
    zh: {
      title: "水产养殖项目与供应链案例 | VARS",
      description: "了解VARS的水产养殖项目、孵化场试验、鲑鱼卵出口、活饵优化和国际供应链成果。",
    },
    es: {
      title: "Proyectos acuícolas y casos de cadena de suministro | VARS",
      description:
        "Conozca los proyectos de VARS, ensayos de criadero, exportación de ovas de salmón, optimización de alimento vivo y logística internacional.",
    },
    fr: {
      title: "Projets aquacoles et études de cas d'approvisionnement | VARS",
      description:
        "Découvrez les projets aquacoles de VARS, les essais en écloserie, les exports d'œufs de saumon, l'optimisation des proies vivantes et les résultats obtenus à l'international.",
    },
    fa: {
      title: "پروژه‌های آبزی‌پروری و نمونه‌کارهای زنجیره تأمین | VARS",
      description:
        "مرور پروژه‌های آبزی‌پروری VARS، آزمون‌های هچری، صادرات تخم ماهی آزاد، بهینه‌سازی غذای زنده و نتایج زنجیره تأمین بین‌المللی.",
    },
  },
  seafood: {
    en: {
      title: "Mediterranean Seafood Export from Türkiye | VARS Aquaculture",
      description:
        "B2B export of fresh sea bass, sea bream, trout, shrimp and other Mediterranean seafood from Türkiye with cold-chain logistics and documentation.",
    },
    tr: {
      title: "Türkiye'den Su Ürünleri İhracatı | VARS",
      description:
        "Levrek, çipura, alabalık, karides ve diğer Akdeniz su ürünleri için B2B tedarik, soğuk zincir ve ihracat süreçlerini inceleyin.",
    },
    de: {
      title: "Export mediterraner Meeresfrüchte aus der Türkei | VARS",
      description:
        "B2B-Export von Wolfsbarsch, Dorade, Forelle, Garnelen und weiteren mediterranen Meeresfrüchten mit Kühlkette und Dokumentation.",
    },
    ar: {
      title: "تصدير المأكولات البحرية المتوسطية من تركيا | VARS",
      description:
        "تصدير B2B لسمك القاروص والدنيس والتراوت والروبيان والمأكولات البحرية المتوسطية مع سلسلة تبريد ووثائق كاملة.",
    },
    ru: {
      title: "Экспорт средиземноморских морепродуктов из Турции | VARS",
      description:
        "B2B-поставки сибаса, дорадо, форели, креветки и других морепродуктов со сквозной холодовой цепью и документами.",
    },
    ja: {
      title: "トルコ発・地中海水産物の輸出 | VARS Aquaculture",
      description:
        "スズキ、タイ、マス、エビなどの地中海水産物を、コールドチェーンと輸出書類付きでB2B供給します。",
    },
    ko: {
      title: "튀르키예산 지중해 수산물 수출 | VARS Aquaculture",
      description:
        "농어, 도미, 송어, 새우 등 지중해 수산물을 콜드체인과 수출 서류를 갖춰 B2B로 공급합니다.",
    },
    zh: {
      title: "土耳其地中海海鲜出口 | VARS Aquaculture",
      description: "提供鲈鱼、鲷鱼、鳟鱼、虾及其他地中海海鲜的B2B出口，并配套冷链物流和完整文件。",
    },
    es: {
      title: "Exportación de marisco mediterráneo desde Türkiye | VARS",
      description:
        "Exportación B2B de lubina, dorada, trucha, gambas y otros productos mediterráneos con cadena de frío y documentación.",
    },
    fr: {
      title: "Export de produits de la mer de Méditerranée depuis la Türkiye | VARS",
      description:
        "Export B2B de bar, daurade royale, truite, crevettes et autres produits de la mer de Méditerranée depuis la Türkiye, avec logistique sous chaîne du froid et documentation.",
    },
    fa: {
      title: "صادرات آبزیان مدیترانه‌ای از ترکیه | VARS Aquaculture",
      description:
        "صادرات B2B سی‌باس، سی‌بریم، قزل‌آلا، میگو و دیگر آبزیان مدیترانه‌ای تازه از ترکیه، همراه با لجستیک زنجیره سرد و مدارک کامل.",
    },
  },
  seafoodEurope: {
    en: {
      title: "Seafood Export from Türkiye to Europe | VARS",
      description:
        "Fresh and frozen sea bass, sea bream, meagre and amberjack from Türkiye to the EU, with veterinary certification, batch traceability and cold chain.",
    },
    tr: {
      title: "Türkiye'den Avrupa'ya Su Ürünleri İhracatı | VARS",
      description:
        "Türkiye'den Avrupa'ya taze ve dondurulmuş su ürünleri tedariki, parti izlenebilirliği, ihracat belgeleri ve soğuk zincir süreçleri.",
    },
    de: {
      title: "Meeresfrüchteexport aus der Türkei nach Europa | VARS",
      description:
        "Wolfsbarsch, Dorade, Umberfisch und Bernsteinmakrele aus der Türkei in die EU — mit Veterinärbescheinigung, Chargenrückverfolgbarkeit und Kühlkette.",
    },
    ar: {
      title: "تصدير المأكولات البحرية من تركيا إلى أوروبا | VARS",
      description:
        "تصدير القاروص والدنيس والشيرن والزيتية طازجة ومجمدة من تركيا إلى الاتحاد الأوروبي، مع الشهادات البيطرية وتتبع الدفعات وسلسلة التبريد.",
    },
    ru: {
      title: "Экспорт морепродуктов из Турции в Европу | VARS",
      description:
        "Экспорт охлаждённых и замороженных сибаса, дорадо, умбрины и лакедры из Турции в ЕС с ветеринарной сертификацией, прослеживаемостью партий и холодовой цепью.",
    },
    ja: {
      title: "トルコから欧州への水産物輸出 | VARS",
      description:
        "トルコからEUへ、スズキ・タイ・イシモチ・カンパチを生鮮および冷凍で輸出。衛生証明、ロット単位のトレーサビリティ、コールドチェーンに対応します。",
    },
    ko: {
      title: "튀르키예에서 유럽으로 수산물 수출 | VARS",
      description:
        "튀르키예에서 EU로 농어, 도미, 민어, 부시리를 신선·냉동으로 수출합니다. 수의 증명, 배치 추적성, 콜드체인을 갖췄습니다.",
    },
    zh: {
      title: "从土耳其向欧洲出口海鲜 | VARS",
      description:
        "从土耳其向欧盟出口冰鲜与冷冻的鲈鱼、鲷鱼、石首鱼及琥珀鱼，配套兽医证书、批次可追溯与冷链物流。",
    },
    es: {
      title: "Exportación de marisco de Türkiye a Europa | VARS",
      description:
        "Exportación de lubina, dorada, corvina y pez limón, frescos y congelados, de Türkiye a la UE, con certificación veterinaria, trazabilidad por lote y cadena de frío.",
    },
    fr: {
      title: "Export de produits de la mer de Türkiye vers l'Europe | VARS",
      description:
        "Bar, daurade royale, maigre et sériole frais ou congelés, de la Türkiye vers l'UE, avec certification vétérinaire, traçabilité des lots et chaîne du froid.",
    },
    fa: {
      title: "صادرات آبزیان از ترکیه به اروپا | VARS",
      description:
        "سی‌باس، سی‌بریم، میگر و سریولای تازه و منجمد، صادرشده از ترکیه به اتحادیه اروپا، با گواهی دامپزشکی، ردیابی بچ و لجستیک زنجیره سرد.",
    },
  },
  artemiaGuide: {
    en: {
      title: "Artemia Cysts Hatching & Incubation Guide | VARS",
      description:
        "Practical Artemia hatching guidance covering salinity, temperature, light, aeration, harvest and hatchery handling, for ≥ 85% hatch-out.",
    },
    tr: {
      // Titles say "yumurta", never "kist" — docs/project/localization/agents-and-tone.md § Turkish.
      title: "Artemia Yumurtası İnkübasyon Kılavuzu | VARS",
      description:
        "Artemia yumurtası açılımında tuzluluk, sıcaklık, ışık, havalandırma, hasat ve kuluçkahane uygulamalarını adım adım inceleyin.",
    },
    de: {
      title: "Artemia-Zysten: Schlupf- und Inkubationsanleitung | VARS",
      description:
        "Praxisleitfaden zum Schlupf von Artemia: Salzgehalt, Temperatur, Licht, Belüftung, Ernte und Handhabung in der Brüterei — für ≥ 85 % Schlupfrate.",
    },
    ar: {
      title: "دليل تفقيس وحضانة حويصلات الأرتيميا | VARS",
      description:
        "إرشادات عملية لتفقيس الأرتيميا تغطي الملوحة ودرجة الحرارة والإضاءة والتهوية والحصاد والتعامل داخل المفرخ — لتحقيق معدل تفقيس ≥ 85%.",
    },
    ru: {
      title: "Инкубация цист артемии: практическое руководство | VARS",
      description:
        "Практическое руководство по выклеву артемии: солёность, температура, освещение, аэрация, сбор науплиев и работа в инкубаторе — для выклева ≥ 85%.",
    },
    ja: {
      title: "アルテミア耐久卵の孵化・インキュベーション手引き | VARS",
      description:
        "塩分、水温、照明、通気、収穫、種苗場での取り扱いまでを網羅したアルテミア孵化の実務ガイド。孵化率85%以上を目標とします。",
    },
    ko: {
      title: "아르테미아 내구란 부화·배양 가이드 | VARS",
      description:
        "염분, 수온, 조명, 폭기, 수확 및 부화장 취급까지 다루는 아르테미아 부화 실무 가이드 — 부화율 85% 이상을 목표로 합니다.",
    },
    zh: {
      title: "丰年虫卵孵化与孵育指南 | VARS",
      description:
        "涵盖盐度、温度、光照、充气、收集与育苗场操作的丰年虫孵化实务指南，目标孵化率 ≥ 85%。",
    },
    es: {
      title: "Guía de eclosión e incubación de quistes de Artemia | VARS",
      description:
        "Guía práctica de eclosión de Artemia: salinidad, temperatura, luz, aireación, cosecha y manejo en criadero, para una eclosión ≥ 85%.",
    },
    fr: {
      title: "Guide d'éclosion et d'incubation des cystes d'artémia | VARS",
      description:
        "Conseils pratiques d'éclosion de l'artémia : salinité, température, lumière, aération, récolte et manipulation en écloserie, pour un taux d'éclosion ≥ 85 %.",
    },
    fa: {
      title: "راهنمای هچ و انکوباسیون سیست آرتمیا | VARS",
      description:
        "راهنمای عملی هچ آرتمیا شامل شوری، دما، نور، هوادهی، برداشت و مدیریت هچری، برای دستیابی به هچ ≥ 85%.",
    },
  },
  decapGuide: {
    en: {
      title: "Standard vs Decapsulated Artemia Cysts | VARS",
      description:
        "Compare shell-on and decapsulated Artemia cysts — protein, lipid and energy per gram, hatching setup, and which suits hatcheries, aquariums and retailers.",
    },
    tr: {
      title: "Standart ve Dekapsüle Artemia Yumurtası | VARS",
      description:
        "Standart ve dekapsüle Artemia yumurtalarını besin yapısı, hazırlama gereksinimi ve kullanım alanlarına göre karşılaştırın.",
    },
    de: {
      title: "Standard- oder dekapsulierte Artemia-Zysten? | VARS",
      description:
        "Vergleich von Artemia-Zysten mit und ohne Schale: Protein, Lipide und Energie je Gramm, Schlupfaufwand und die passende Wahl für Brüterei, Aquarium und Handel.",
    },
    ar: {
      title: "حويصلات الأرتيميا القياسية مقابل منزوعة القشرة | VARS",
      description:
        "قارن بين الأرتيميا بقشرتها ومنزوعة القشرة: البروتين والدهون والطاقة لكل غرام، ومتطلبات التفقيس، والأنسب للمفرخات وأحواض الزينة والتجزئة.",
    },
    ru: {
      title: "Обычные или декапсулированные цисты артемии | VARS",
      description:
        "Сравнение цист артемии в оболочке и декапсулированных: белок, липиды и энергия на грамм, требования к выклеву и что выбрать инкубатору, аквариуму или рознице.",
    },
    ja: {
      title: "通常卵と脱殻アルテミアの比較 | VARS",
      description:
        "殻付きと脱殻アルテミアを比較。1グラムあたりのたんぱく質・脂質・エネルギー、孵化設備の要否、種苗場・水族館・小売それぞれに適した形態を解説します。",
    },
    ko: {
      title: "일반 내구란과 탈각 아르테미아 비교 | VARS",
      description:
        "껍질이 있는 내구란과 탈각 아르테미아 비교 — 그램당 단백질·지질·열량, 부화 설비 필요 여부, 부화장·아쿠아리움·리테일별 적합성.",
    },
    zh: {
      title: "普通卵与去壳丰年虫卵对比 | VARS",
      description:
        "对比带壳与去壳丰年虫卵：每克蛋白质、脂质与能量，孵化设备需求，以及育苗场、水族与零售各自的适用形态。",
    },
    es: {
      title: "Quistes de Artemia estándar frente a descapsulados | VARS",
      description:
        "Compare la Artemia con cáscara y descapsulada: proteína, lípidos y energía por gramo, equipo de eclosión y cuál conviene a criaderos, acuarios y minoristas.",
    },
    fr: {
      title: "Cystes d'artémia standard ou décapsulés | VARS",
      description:
        "Comparez les cystes d'artémia avec coque et décapsulés — protéines, lipides et énergie par gramme, installation d'éclosion, et lequel convient aux écloseries, aux aquariums et aux revendeurs.",
    },
    fa: {
      title: "سیست آرتمیای معمولی در برابر دکپسوله | VARS",
      description:
        "مقایسه سیست آرتمیای پوسته‌دار و دکپسوله — پروتئین، چربی و انرژی در هر گرم، تجهیزات هچ، و اینکه کدام برای هچری، آکواریوم و خرده‌فروشی مناسب است.",
    },
  },
  contact: {
    en: {
      title: "Contact VARS Aquaculture | Global Export Desk",
      description:
        "Contact VARS in İzmir, Türkiye about salmon eggs, Artemia live feed, aquaculture inputs, seafood exports, and technical support.",
    },
    tr: {
      title: "İletişim ve İhracat Masası | VARS",
      description:
        "Canlı yem, somon yumurtası, yem girdileri, su ürünleri ihracatı ve teknik destek için İzmir'deki VARS Su Ürünleri ekibine ulaşın.",
    },
    de: {
      title: "Kontakt zu VARS Aquaculture | Globaler Export",
      description:
        "Kontaktieren Sie VARS in İzmir zu Lachseiern, Artemia-Lebendfutter, Aquakulturbedarf, Meeresfrüchteexporten und technischer Unterstützung.",
    },
    ar: {
      title: "تواصل مع VARS للاستزراع المائي | المبيعات العالمية",
      description:
        "تواصل مع فريق VARS في إزمير بشأن بيض السلمون والأرتيميا ومدخلات الاستزراع المائي وتصدير المأكولات البحرية والدعم الفني.",
    },
    ru: {
      title: "Связаться с VARS Aquaculture | Глобальный экспорт",
      description:
        "Свяжитесь с VARS в Измире по вопросам икры лосося, живого корма артемии, аквакультуры, морепродуктов и технической поддержки.",
    },
    ja: {
      title: "VARS Aquacultureへのお問い合わせ | 海外輸出窓口",
      description:
        "サーモン卵、アルテミア、生産資材、水産物輸出、技術サポートについてイズミルのVARSへご連絡ください。",
    },
    ko: {
      title: "VARS Aquaculture 문의 | 글로벌 수출",
      description:
        "연어 수정란, 알테미아 생먹이, 양식 자재, 수산물 수출 및 기술 지원은 이즈미르 VARS에 문의하세요.",
    },
    zh: {
      title: "联系VARS水产养殖 | 全球出口",
      description:
        "如需鲑鱼卵、丰年虫活饵、水产养殖投入品、海鲜出口或技术支持，请联系位于伊兹密尔的VARS。",
    },
    es: {
      title: "Contacte con VARS Aquaculture | Exportación global",
      description:
        "Contacte con VARS en Esmirna para ovas de salmón, alimento vivo Artemia, insumos acuícolas, exportación de marisco y soporte técnico.",
    },
    fr: {
      title: "Contacter VARS Aquaculture | Service export international",
      description:
        "Contactez VARS à İzmir, en Türkiye, au sujet des œufs de saumon, des proies vivantes à base d'artémia, des intrants aquacoles, de l'export de produits de la mer et de l'appui technique.",
    },
    fa: {
      title: "تماس با VARS Aquaculture | میز صادرات جهانی",
      description:
        "برای تخم ماهی آزاد، غذای زنده آرتمیا، نهاده‌های آبزی‌پروری، صادرات آبزیان و پشتیبانی فنی با VARS در ازمیر ترکیه تماس بگیرید.",
    },
  },

  /*
   * Everything below moved up out of GENERIC_SEO, which is English-only. Any
   * key left there reaches the eight other locales through localizedGeneric(),
   * which serves the English title and description with a brand suffix bolted
   * on — the exact failure the `aquariums` comment above documents being fixed
   * once already. It was still live on nine more keys, so /tr/faqs was
   * answering Turkish buyers with "Aquaculture FAQs | VARS Aquaculture |
   * VARS Aquaculture" and /tr/horeca-seafood-middle-east with English.
   *
   * Only indexable pages were moved. login/register/account/cart/checkout stay
   * in GENERIC_SEO because the root's isPrivate regex marks them noindex, so
   * their metadata is never read by a search engine.
   */

  quote: {
    en: {
      title: "Request an Aquaculture Quote | VARS",
      description:
        "Request a tailored quote for live feed, salmon eggs, feed additives, or Mediterranean seafood export from VARS.",
    },
    tr: {
      title: "Su Ürünleri Teklifi Alın | VARS",
      description:
        "Canlı yem, somon yumurtası, yem girdileri ve Akdeniz su ürünleri için şirketinize ve teslimat ihtiyacınıza özel teklif isteyin.",
    },
    de: {
      title: "Angebot für Aquakultur anfordern | VARS",
      description:
        "Fordern Sie ein individuelles Angebot für Lebendfutter, Lachseier, Futtermittelzusätze oder den Export mediterraner Fischereierzeugnisse an.",
    },
    ar: {
      title: "اطلب عرض سعر لتربية الأحياء المائية | VARS",
      description:
        "اطلب من VARS عرض سعر مخصصاً للأعلاف الحية وبيض السلمون وإضافات الأعلاف وتصدير الأسماك المتوسطية.",
    },
    ru: {
      title: "Запрос коммерческого предложения | VARS",
      description:
        "Запросите индивидуальное предложение на живые корма, икру лосося, кормовые добавки и экспорт средиземноморской рыбы.",
    },
    ja: {
      title: "水産養殖のお見積り依頼 | VARS",
      description:
        "生餌、サケ卵、飼料添加物、地中海産水産物の輸出について、VARSより個別のお見積りをご提案いたします。",
    },
    ko: {
      title: "양식 견적 요청 | VARS",
      description:
        "생사료, 연어 발안란, 사료 첨가제, 지중해 수산물 수출에 대한 맞춤 견적을 VARS에 요청하십시오.",
    },
    zh: {
      title: "水产养殖询价 | VARS",
      description: "就鲜活饵料、鲑鱼卵、饲料添加剂及地中海水产品出口，向 VARS 索取定制报价。",
    },
    es: {
      title: "Solicite un presupuesto de acuicultura | VARS",
      description:
        "Solicite un presupuesto a medida de alimento vivo, ovas de salmón, aditivos para piensos o exportación de pescado mediterráneo.",
    },
    fr: {
      title: "Demander un devis aquacole | VARS",
      description:
        "Demandez un devis sur mesure pour des proies vivantes, des œufs de saumon, des additifs alimentaires ou l'export de produits de la mer de Méditerranée auprès de VARS.",
    },
    fa: {
      title: "درخواست استعلام قیمت آبزی‌پروری | VARS",
      description:
        "برای غذای زنده، تخم ماهی آزاد، افزودنی‌های خوراک یا صادرات آبزیان مدیترانه‌ای از VARS استعلام اختصاصی دریافت کنید.",
    },
  },

  faq: {
    en: {
      title: "Aquaculture FAQs | VARS Aquaculture",
      description:
        "Answers about aquaculture orders, salmon ova, Artemia, minimum quantities, certification, cold-chain delivery, and export.",
    },
    tr: {
      title: "Su Ürünleri Sıkça Sorulan Sorular | VARS",
      description:
        "Sipariş, Artemia yumurtası, somon yumurtası, minimum sipariş, belgeler, soğuk zincir teslimatı ve ihracat sorularına yanıt bulun.",
    },
    de: {
      title: "Häufige Fragen zur Aquakultur | VARS",
      description:
        "Antworten zu Bestellungen, Lachseiern, Artemia-Zysten, Mindestabnahmemengen, Zertifizierung, Kühlkettenlieferung und Export.",
    },
    ar: {
      title: "الأسئلة الشائعة حول تربية الأحياء المائية | VARS",
      description:
        "إجابات حول الطلبات وبيض السلمون وحويصلات الأرتيميا والحد الأدنى للكميات والشهادات والتسليم بسلسلة التبريد والتصدير.",
    },
    ru: {
      title: "Часто задаваемые вопросы | VARS Aquaculture",
      description:
        "Ответы о заказах, икре лосося, цистах артемии, минимальных партиях, сертификации, доставке в холодовой цепи и экспорте.",
    },
    ja: {
      title: "水産養殖のよくあるご質問 | VARS",
      description:
        "ご発注、サケ卵、アルテミア耐久卵、最小ロット、認証、コールドチェーン配送、輸出に関するご質問にお答えします。",
    },
    ko: {
      title: "양식 자주 묻는 질문 | VARS",
      description:
        "발주, 연어 발안란, 아르테미아 내구란, 최소 주문 수량, 인증, 콜드체인 배송, 수출에 관한 답변입니다.",
    },
    zh: {
      title: "水产养殖常见问题 | VARS",
      description: "关于订购、鲑鱼卵、丰年虫卵、最小起订量、认证、冷链配送与出口的解答。",
    },
    es: {
      title: "Preguntas frecuentes de acuicultura | VARS",
      description:
        "Respuestas sobre pedidos, ovas de salmón, quistes de artemia, cantidades mínimas, certificación, entrega en cadena de frío y exportación.",
    },
    fr: {
      title: "Questions fréquentes sur l'aquaculture | VARS Aquaculture",
      description:
        "Réponses sur les commandes aquacoles, les œufs de salmonidés, l'artémia, les quantités minimales, la certification, la livraison sous chaîne du froid et l'export.",
    },
    fa: {
      title: "پرسش‌های متداول آبزی‌پروری | VARS Aquaculture",
      description:
        "پاسخ پرسش‌ها درباره سفارش، تخم ماهی آزاد، آرتمیا، حداقل مقدار سفارش، گواهی‌ها، تحویل با زنجیره سرد و صادرات.",
    },
  },

  salmonOva: {
    en: {
      title: "Salmonid Ova Solutions | Atlantic, Coho & Trout Eggs | VARS",
      description:
        "Certified fertilized salmon and trout eggs with cold-chain logistics for hatcheries and RAS farms.",
    },
    tr: {
      title: "Somon ve Alabalık Yumurtası Çözümleri | VARS",
      description:
        "Kuluçkahaneler ve RAS tesisleri için döllenmiş somon ve alabalık yumurtası tedariki ile soğuk zincir lojistiğini inceleyin.",
    },
    de: {
      title: "Salmoniden-Eier | Atlantik, Coho und Forelle | VARS",
      description:
        "Zertifizierte befruchtete Lachs- und Forelleneier mit Kühlkettenlogistik für Brutanlagen und Kreislaufanlagen (RAS).",
    },
    ar: {
      title: "حلول بيض السلمونيات | الأطلسي والكوهو والتراوت | VARS",
      description:
        "بيض سلمون وتراوت ملقّح ومعتمد مع لوجستيات سلسلة التبريد لمفارخ الأسماك وأنظمة الاستزراع المغلقة (RAS).",
    },
    ru: {
      title: "Икра лососевых | Атлантический лосось, кижуч и форель | VARS",
      description:
        "Сертифицированная оплодотворённая икра лосося и форели с логистикой холодовой цепи для инкубаториев и УЗВ.",
    },
    ja: {
      title: "サケ科魚類の発眼卵ソリューション | サケ・トラウト | VARS",
      description:
        "孵化場および閉鎖循環式(RAS)養殖場向けに、コールドチェーン輸送で認証済みのサケ・トラウト受精卵をお届けします。",
    },
    ko: {
      title: "연어과 발안란 솔루션 | 대서양연어·은연어·송어 | VARS",
      description:
        "부화장 및 순환여과식(RAS) 양식장을 위한 인증 수정란 — 콜드체인 물류로 공급합니다.",
    },
    zh: {
      title: "鲑科鱼卵解决方案 | 大西洋鲑、银鲑与鳟鱼 | VARS",
      description: "面向孵化场与循环水养殖系统（RAS）的认证受精鲑鱼卵与鳟鱼卵，全程冷链物流。",
    },
    es: {
      title: "Soluciones en ovas de salmónidos | Salmón y trucha | VARS",
      description:
        "Ovas fecundadas certificadas de salmón y trucha con logística de cadena de frío para criaderos y sistemas RAS.",
    },
    fr: {
      title: "Œufs de salmonidés | saumon atlantique, coho et truite | VARS",
      description:
        "Œufs fécondés de saumon et de truite certifiés, avec logistique sous chaîne du froid, pour les écloseries et les fermes en RAS.",
    },
    fa: {
      title: "راهکارهای تخم ماهیان آزاد | آتلانتیک، کوهو و قزل‌آلا | VARS",
      description:
        "تخم بارورشده و گواهی‌شده ماهی آزاد و قزل‌آلا با لجستیک زنجیره سرد، برای هچری‌ها و مزارع مداربسته.",
    },
  },

  /*
   * The English description used to end "with TRACES, CoA and ASC
   * documentation". ASC is dropped here rather than translated eight times:
   * docs/project/evidence/credentials-and-claims.md holds that a credential goes on the site
   * when the certificate exists, and commit 5344a6a already struck ASC and
   * GlobalG.A.P from the EU export page as unbacked. Veterinary health
   * certification and TRACES pre-notification are real and verifiable, and
   * TRACES is a notification system rather than a certification — so the
   * wording follows the approved palette in that doc rather than repeating
   * "TRACES certified".
   */
  oliveFlounderExport: {
    en: {
      title: "Olive Flounder Export | Frozen, Fresh & Live | VARS",
      description:
        "Olive Flounder (Paralichthys olivaceus) for global export — frozen fillets, fresh gutted and live, with veterinary health cert, CoA and TRACES notice.",
    },
    tr: {
      title: "Japon Pisi Balığı İhracatı | VARS",
      description:
        "Japon pisi balığının canlı, taze ve dondurulmuş ürün biçimleri ile veterinerlik belgeleri ve ihracat lojistiğini inceleyin.",
    },
    de: {
      title: "Olivenflunder-Export | Gefroren, frisch und lebend | VARS",
      description:
        "Olivenflunder (Paralichthys olivaceus) für den Export — Tiefkühlfilet, frisch ausgenommen und lebend, mit Veterinärbescheinigung, CoA und TRACES.",
    },
    ar: {
      title: "تصدير المفلطح الزيتوني | مجمّد وطازج وحي | VARS",
      description:
        "المفلطح الزيتوني (Paralichthys olivaceus) للتصدير العالمي — شرائح مجمّدة وطازج منظّف وحي، مع الشهادة الصحية البيطرية وشهادة التحليل والإخطار المسبق عبر TRACES.",
    },
    ru: {
      title: "Экспорт оливковой камбалы | Мороженая, свежая и живая | VARS",
      description:
        "Оливковая камбала (Paralichthys olivaceus) на экспорт — мороженое филе, свежая потрошёная и живая, с ветеринарным сертификатом, CoA и предуведомлением TRACES.",
    },
    ja: {
      title: "ヒラメの輸出 | 冷凍・生鮮・活魚 | VARS",
      description:
        "ヒラメ(Paralichthys olivaceus)を世界へ輸出。冷凍フィレ、生鮮、活魚に対応し、検疫証明書、CoA、TRACES通知を備えます。",
    },
    ko: {
      title: "광어 수출 | 냉동·선어·활어 | VARS",
      description:
        "광어(Paralichthys olivaceus) 글로벌 수출 — 냉동 필렛, 선어, 활어. 검역증명서, CoA, TRACES 사전신고 구비.",
    },
    zh: {
      title: "牙鲆出口 | 冷冻、鲜品与活鱼 | VARS",
      description:
        "牙鲆（Paralichthys olivaceus）面向全球出口——冷冻鱼片、鲜品去脏与活鱼，配备兽医卫生证书、CoA 及 TRACES 预通报。",
    },
    es: {
      title: "Exportación de Olive Flounder | Congelado y fresco | VARS",
      description:
        "Olive Flounder (Paralichthys olivaceus) para exportación — filete congelado, fresco eviscerado y vivo, con certificado sanitario, CoA y TRACES.",
    },
    fr: {
      title: "Export de cardeau hirame | congelé, frais et vivant | VARS",
      description:
        "Cardeau hirame (Paralichthys olivaceus) pour l'export mondial — filets congelés, entier vidé frais et vivant, avec certificat sanitaire vétérinaire, certificat d'analyse et notification TRACES.",
    },
    fa: {
      title: "صادرات کفشک زیتونی | منجمد، تازه و زنده | VARS",
      description:
        "کفشک زیتونی (Paralichthys olivaceus) برای صادرات جهانی — فیله منجمد، تازه شکم‌خالی و زنده، همراه با گواهی سلامت دامپزشکی، CoA و اعلان پیشین TRACES.",
    },
  },

  horeca: {
    en: {
      title: "HORECA Seafood Supply in the Middle East | VARS",
      description:
        "B2B supply of Mediterranean sea bass, sea bream and shrimp to hotels, restaurants, and catering buyers across the GCC.",
    },
    tr: {
      title: "Orta Doğu HORECA Su Ürünleri Tedariki | VARS",
      description:
        "Orta Doğu'daki otel, restoran ve catering alıcıları için Akdeniz levreği, çipura ve karides B2B tedarik seçeneklerini inceleyin.",
    },
    de: {
      title: "HORECA-Seafood-Versorgung im Nahen Osten | VARS",
      description:
        "B2B-Belieferung von Hotels, Restaurants und Catering-Einkäufern in den Golfstaaten mit mediterranem Wolfsbarsch, Goldbrasse und Garnelen.",
    },
    ar: {
      title: "توريد المأكولات البحرية لقطاع الضيافة في الشرق الأوسط | VARS",
      description:
        "توريد B2B للقاروص والدنيس المتوسطي والروبيان إلى الفنادق والمطاعم وشركات التموين في دول الخليج.",
    },
    ru: {
      title: "Поставки морепродуктов для HORECA на Ближнем Востоке | VARS",
      description:
        "B2B-поставки средиземноморского сибаса, дорады и креветки отелям, ресторанам и кейтерингу стран Персидского залива.",
    },
    ja: {
      title: "中東HORECA向け水産物供給 | VARS",
      description:
        "湾岸諸国のホテル、レストラン、ケータリング事業者へ、地中海産スズキ、ヨーロッパヘダイ、エビをB2Bで供給いたします。",
    },
    ko: {
      title: "중동 HORECA 수산물 공급 | VARS",
      description:
        "걸프 지역 호텔·레스토랑·케이터링 바이어를 위한 지중해 농어, 도미, 새우 B2B 공급.",
    },
    zh: {
      title: "中东 HORECA 水产供应 | VARS",
      description: "面向海湾地区酒店、餐厅与餐饮采购商，B2B 供应地中海鲈鱼、鲷鱼与虾类。",
    },
    es: {
      title: "Suministro HORECA de pescado en Oriente Medio | VARS",
      description:
        "Suministro B2B de lubina y dorada mediterráneas y langostino a hoteles, restaurantes y caterings del Golfo.",
    },
    fr: {
      title: "Approvisionnement CHR en produits de la mer au Moyen-Orient | VARS",
      description:
        "Approvisionnement B2B en bar, daurade royale et crevettes de Méditerranée pour les hôtels, les restaurants et les acheteurs de la restauration collective du CCG.",
    },
    fa: {
      title: "تأمین آبزیان HORECA در خاورمیانه | VARS",
      description:
        "تأمین B2B سی‌باس، سی‌بریم و میگوی مدیترانه‌ای برای هتل‌ها، رستوران‌ها و خریداران کترینگ در سراسر کشورهای شورای همکاری خلیج فارس.",
    },
  },

  regionalTrade: {
    en: {
      title: "Aquaculture Trade Routes: Middle East & Europe | VARS",
      description:
        "Aquaculture live feed, salmon eggs, and seafood export routes from Türkiye to the Middle East and Europe.",
    },
    tr: {
      title: "Orta Doğu ve Avrupa Su Ürünleri Ticareti | VARS",
      description:
        "Türkiye'den Orta Doğu ve Avrupa'ya canlı yem, somon yumurtası ve su ürünleri ticareti ile lojistik bağlantılarını inceleyin.",
    },
    de: {
      title: "Handelsrouten der Aquakultur: Naher Osten und Europa | VARS",
      description:
        "Exportrouten für Lebendfutter, Lachseier und Fischereierzeugnisse von der Türkei in den Nahen Osten und nach Europa.",
    },
    ar: {
      title: "طرق تجارة الاستزراع المائي: الشرق الأوسط وأوروبا | VARS",
      description:
        "طرق تصدير الأعلاف الحية وبيض السلمون والمأكولات البحرية من تركيا إلى الشرق الأوسط وأوروبا.",
    },
    ru: {
      title: "Торговля в аквакультуре: Ближний Восток и Европа | VARS",
      description:
        "Экспортные маршруты живых кормов, икры лосося и морепродуктов из Турции на Ближний Восток и в Европу.",
    },
    ja: {
      title: "水産養殖の貿易ルート:中東と欧州 | VARS",
      description:
        "トルコから中東および欧州へ、生餌、サケ卵、水産物を輸送する輸出ルートをご紹介します。",
    },
    ko: {
      title: "양식 교역 루트: 중동과 유럽 | VARS",
      description: "튀르키예에서 중동 및 유럽으로 이어지는 생사료, 연어 발안란, 수산물 수출 경로.",
    },
    zh: {
      title: "水产养殖贸易通道：中东与欧洲 | VARS",
      description: "自土耳其通往中东与欧洲的鲜活饵料、鲑鱼卵及水产品出口通道。",
    },
    es: {
      title: "Comercio acuícola: Oriente Medio y Europa | VARS",
      description:
        "Rutas de exportación de alimento vivo, ovas de salmón y productos del mar desde Türkiye hacia Oriente Medio y Europa.",
    },
    fr: {
      title: "Routes commerciales aquacoles : Moyen-Orient et Europe | VARS",
      description:
        "Routes d'export des proies vivantes aquacoles, des œufs de saumon et des produits de la mer, de la Türkiye vers le Moyen-Orient et l'Europe.",
    },
    fa: {
      title: "مسیرهای تجاری آبزی‌پروری: خاورمیانه و اروپا | VARS",
      description:
        "مسیرهای صادرات غذای زنده آبزی‌پروری، تخم ماهی آزاد و آبزیان از ترکیه به خاورمیانه و اروپا.",
    },
  },

  shop: {
    en: {
      title: "Aquaculture Store | VARS Aquaculture",
      description:
        "Browse aquaculture feed, hatchery inputs, and Mediterranean seafood products from VARS.",
    },
    tr: {
      title: "Su Ürünleri Mağazası | VARS",
      description:
        "Su ürünleri yemlerini, kuluçkahane girdilerini ve mağazada sunulan deniz ürünlerini güncel fiyat ve stok bilgileriyle inceleyin.",
    },
    de: {
      title: "Aquakultur-Shop | VARS Aquaculture",
      description:
        "Aquakulturfutter, Brutanlagen-Bedarf und mediterrane Fischereierzeugnisse von VARS entdecken.",
    },
    ar: {
      title: "متجر الاستزراع المائي | VARS Aquaculture",
      description:
        "تصفّح أعلاف الاستزراع المائي ومستلزمات المفارخ والمأكولات البحرية المتوسطية من VARS.",
    },
    ru: {
      title: "Магазин для аквакультуры | VARS Aquaculture",
      description:
        "Корма для аквакультуры, продукция для инкубаториев и средиземноморские морепродукты от VARS.",
    },
    ja: {
      title: "水産養殖ストア | VARS Aquaculture",
      description: "VARSの養殖用飼料、孵化場向け資材、地中海産水産物をご覧いただけます。",
    },
    ko: {
      title: "양식 스토어 | VARS Aquaculture",
      description: "VARS의 양식 사료, 부화장 자재, 지중해 수산물을 둘러보십시오.",
    },
    zh: {
      title: "水产养殖商城 | VARS Aquaculture",
      description: "浏览 VARS 的养殖饲料、孵化场投入品与地中海水产品。",
    },
    es: {
      title: "Tienda de acuicultura | VARS Aquaculture",
      description:
        "Explore piensos de acuicultura, insumos para criaderos y productos del mar mediterráneos de VARS.",
    },
    fr: {
      title: "Boutique aquacole | VARS Aquaculture",
      description:
        "Parcourez les aliments aquacoles, les intrants d'écloserie et les produits de la mer de Méditerranée proposés par VARS.",
    },
    fa: {
      title: "فروشگاه آبزی‌پروری | VARS Aquaculture",
      description:
        "خوراک آبزی‌پروری، نهاده‌های هچری و محصولات آبزیان مدیترانه‌ای را در فروشگاه مرور کنید.",
    },
  },

  privacy: {
    en: {
      title: "Privacy Policy | VARS Aquaculture",
      description:
        "VARS Aquaculture privacy policy covering personal data, KVKK, GDPR, cookies, and B2B communications.",
    },
    tr: {
      title: "Gizlilik Politikası | VARS",
      description:
        "VARS Su Ürünleri'nin kişisel veriler, KVKK, GDPR, çerezler ve B2B iletişimleri hakkındaki gizlilik politikasını okuyun.",
    },
    de: {
      title: "Datenschutzerklärung | VARS Aquaculture",
      description:
        "Datenschutzerklärung von VARS Aquaculture zu personenbezogenen Daten, KVKK, DSGVO, Cookies und B2B-Kommunikation.",
    },
    ar: {
      title: "سياسة الخصوصية | VARS Aquaculture",
      description:
        "سياسة خصوصية VARS Aquaculture بشأن البيانات الشخصية وقانون KVKK واللائحة العامة لحماية البيانات وملفات تعريف الارتباط والمراسلات التجارية.",
    },
    ru: {
      title: "Политика конфиденциальности | VARS Aquaculture",
      description:
        "Политика конфиденциальности VARS Aquaculture: персональные данные, KVKK, GDPR, файлы cookie и деловая переписка.",
    },
    ja: {
      title: "プライバシーポリシー | VARS Aquaculture",
      description:
        "個人データ、KVKK、GDPR、Cookie、B2B連絡に関するVARS Aquacultureのプライバシーポリシーです。",
    },
    ko: {
      title: "개인정보 처리방침 | VARS Aquaculture",
      description:
        "개인정보, KVKK, GDPR, 쿠키 및 B2B 커뮤니케이션에 관한 VARS Aquaculture 개인정보 처리방침입니다.",
    },
    zh: {
      title: "隐私政策 | VARS Aquaculture",
      description: "VARS Aquaculture 关于个人数据、KVKK、GDPR、Cookie 与 B2B 通信的隐私政策。",
    },
    es: {
      title: "Política de privacidad | VARS Aquaculture",
      description:
        "Política de privacidad de VARS Aquaculture sobre datos personales, KVKK, RGPD, cookies y comunicaciones B2B.",
    },
    fr: {
      title: "Politique de confidentialité | VARS Aquaculture",
      description:
        "Politique de confidentialité de VARS Aquaculture : données personnelles, KVKK, RGPD, cookies et communications B2B.",
    },
    fa: {
      title: "سیاست حفظ حریم خصوصی | VARS Aquaculture",
      description:
        "سیاست حریم خصوصی VARS Aquaculture درباره داده‌های شخصی، KVKK، GDPR، کوکی‌ها و ارتباطات B2B.",
    },
  },

  terms: {
    en: {
      title: "Terms of Service | VARS Aquaculture",
      description:
        "Terms governing VARS Aquaculture website use, B2B quotations, commercial transactions, and online services.",
    },
    tr: {
      title: "Kullanım Koşulları | VARS",
      description:
        "VARS Su Ürünleri web sitesinin kullanımı, B2B teklifler, ticari işlemler ve çevrimiçi hizmetlere ilişkin koşulları okuyun.",
    },
    de: {
      title: "Nutzungsbedingungen | VARS Aquaculture",
      description:
        "Bedingungen für die Nutzung der Website von VARS Aquaculture, B2B-Angebote, Handelsgeschäfte und Online-Dienste.",
    },
    ar: {
      title: "شروط الخدمة | VARS Aquaculture",
      description:
        "الشروط التي تحكم استخدام موقع VARS Aquaculture وعروض الأسعار B2B والمعاملات التجارية والخدمات الإلكترونية.",
    },
    ru: {
      title: "Условия использования | VARS Aquaculture",
      description:
        "Условия использования сайта VARS Aquaculture, коммерческих предложений B2B, торговых операций и онлайн-сервисов.",
    },
    ja: {
      title: "利用規約 | VARS Aquaculture",
      description:
        "VARS Aquacultureのウェブサイト利用、B2B見積り、商取引およびオンラインサービスに関する規約です。",
    },
    ko: {
      title: "이용약관 | VARS Aquaculture",
      description:
        "VARS Aquaculture 웹사이트 이용, B2B 견적, 상거래 및 온라인 서비스에 적용되는 약관입니다.",
    },
    zh: {
      title: "服务条款 | VARS Aquaculture",
      description: "适用于 VARS Aquaculture 网站使用、B2B 报价、商业交易与在线服务的条款。",
    },
    es: {
      title: "Términos del servicio | VARS Aquaculture",
      description:
        "Términos que rigen el uso del sitio web de VARS Aquaculture, las cotizaciones B2B, las transacciones comerciales y los servicios en línea.",
    },
    fr: {
      title: "Conditions générales | VARS Aquaculture",
      description:
        "Conditions régissant l'utilisation du site de VARS Aquaculture, les devis B2B, les transactions commerciales et les services en ligne.",
    },
    fa: {
      title: "شرایط استفاده از خدمات | VARS Aquaculture",
      description:
        "شرایط حاکم بر استفاده از وب‌سایت VARS Aquaculture، استعلام‌های B2B، معاملات تجاری و خدمات آنلاین.",
    },
  },
};

/**
 * English-only metadata, for pages that are deliberately never indexed.
 *
 * getLocalizedPageMeta() reads SEO_COPY first, so a key present in both maps
 * would make the entry here dead weight that drifts out of sync — the reason
 * every indexable page moved out. seo.test.ts asserts the two maps stay
 * disjoint, so adding a key here that SEO_COPY already covers fails the build
 * rather than quietly shipping English to eight locales.
 *
 * Everything remaining is matched by the isPrivate regex in __root.tsx and
 * carries noindex, nofollow, so no crawler ever reads these strings.
 */
const GENERIC_SEO: Partial<Record<SeoPage, PageMeta>> = {
  login: {
    title: "Sign In | VARS Aquaculture Portal",
    description:
      "Sign in to manage your VARS B2B account, quotations, orders, and shipment documentation.",
  },
  register: {
    title: "Register as a Partner | VARS Aquaculture",
    description:
      "Register your company to request B2B pricing, quotations, aquaculture products, and export services.",
  },
  account: {
    title: "Customer Dashboard | VARS Aquaculture",
    description:
      "Manage B2B orders, shipment documentation, quotations, and company profile in the VARS portal.",
  },
  cart: {
    title: "Quote Cart | VARS Aquaculture",
    description:
      "Review selected aquaculture products and prepare a B2B quotation request for VARS.",
  },
  checkout: {
    title: "Store Checkout | VARS Aquaculture",
    description: "Complete your VARS aquaculture store order and delivery details.",
  },
};

const TURKISH_PRIVATE_SEO: Partial<Record<SeoPage, PageMeta>> = {
  login: {
    title: "Giriş Yap | VARS",
    description: "VARS Su Ürünleri B2B hesabınıza giriş yapın.",
  },
  register: {
    title: "İş Ortağı Kaydı | VARS",
    description: "Teklif, sipariş ve portal hizmetleri için şirket hesabınızı oluşturun.",
  },
  account: {
    title: "Müşteri Paneli | VARS",
    description: "Siparişlerinizi, belgelerinizi ve şirket profilinizi yönetin.",
  },
  cart: {
    title: "Teklif Sepeti | VARS",
    description: "B2B teklif talebiniz için seçtiğiniz su ürünleri ve girdileri inceleyin.",
  },
  checkout: {
    title: "Siparişi Tamamla | VARS",
    description: "Siparişinizi ve teslimat bilgilerinizi tamamlayın.",
  },
};

/**
 * Pages whose BODY exists in Turkish and English only.
 *
 * `privacy.tsx` and `terms.tsx` render `lang === "tr" ? <Tr /> : <En />` — there
 * is no translation path for the other eight locales and, being legal
 * instruments, they are not machine-translated into one (VARS-062).
 *
 * Their metadata, however, was written for all ten. The result was a Persian
 * title and description over an English document: the search snippet promised a
 * language the page does not serve. Metadata describes the page that is
 * actually returned, so for these two it follows the body, not the UI locale.
 *
 * `sitemap[.]xml.ts` already restricts both to `["tr", "en"]`; this is the same
 * fact enforced in the second place it is asserted.
 */
export const BILINGUAL_LEGAL_PAGES = ["privacy", "terms"] as const satisfies readonly SeoPage[];

export function isBilingualLegalPage(page: string): boolean {
  return (BILINGUAL_LEGAL_PAGES as readonly string[]).includes(page);
}

export function getLocalizedPageMeta(page: SeoPage): PageMeta {
  if (page === "home" || page === "about" || page === "products" || page === "blog") {
    return getSeoMeta(page);
  }
  const lang = getCurrentLocaleForSeo();
  // Turkish keeps its own copy because the Turkish body is real; every other
  // locale is served the English document and so gets the English metadata.
  if (isBilingualLegalPage(page)) {
    return SEO_COPY[page]?.[lang === "tr" ? "tr" : "en"] ?? localizedGeneric(page, lang);
  }
  if (lang === "tr" && TURKISH_PRIVATE_SEO[page]) return TURKISH_PRIVATE_SEO[page];
  return SEO_COPY[page]?.[lang] ?? localizedGeneric(page, lang);
}

export function getLocalizedMeta(page: SeoPage) {
  const meta = getLocalizedPageMeta(page);
  return metaTags(meta.title, meta.description);
}

/** Metadata for route sections whose visible title/subtitle already live in the locale dictionary. */
export function localizedDictionaryMeta(
  titleKey: string,
  descriptionKey: string,
  fallbackTitle: string,
  fallbackDescription: string,
) {
  const dict = activeDict();
  return metaTags(
    localizedBrandTitle(dict[titleKey] ?? fallbackTitle),
    dict[descriptionKey] ?? fallbackDescription,
  );
}

/**
 * The same six tags, for routes whose title and description are built from
 * loader data rather than from SEO_COPY — product categories, store pages,
 * blog categories.
 *
 * Those routes were emitting only title and description. og:* and twitter:*
 * are not inherited from a sibling tag; when a page omits them, __root's
 * values stand, and __root falls back to the HOMEPAGE copy for any path its
 * map does not know. So every one of those pages was sharing the front page's
 * social card and, on several, the front page's description too.
 */
export function metaTags(title: string, description: string) {
  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];
}

/**
 * Trims a body description down to a search snippet.
 *
 * Product descriptions are written for the page, not for a SERP, and run from
 * 117 to 304 characters. Google renders roughly 155–160, so the long ones were
 * being cut mid-clause by the search engine rather than by us.
 *
 * Whole sentences only. Accumulate while the total stays inside the budget; if
 * the first sentence alone is already over, return it intact rather than
 * truncating it — a clipped sentence with an ellipsis reads worse in a snippet
 * than a slightly long one, and Google will do its own cut anyway. This never
 * invents or rewrites copy, so nothing here needs a content review.
 */
const SCRIPT_BUDGETS: Array<{ test: RegExp; budget: number; floor: number }> = [
  // CJK characters (Chinese, Japanese, Korean) fill SERP pixels much faster (~40-60 chars max)
  { test: /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af]/, budget: 60, floor: 30 },
];

const DEFAULT_BUDGET = 160;
const DEFAULT_FLOOR = 80;

function getSnippetLimits(text: string): { budget: number; floor: number } {
  for (const rule of SCRIPT_BUDGETS) {
    if (rule.test.test(text)) return { budget: rule.budget, floor: rule.floor };
  }
  return { budget: DEFAULT_BUDGET, floor: DEFAULT_FLOOR };
}

export function snippetDescription(text: string): string {
  // Collapse runs of whitespace before measuring. Odoo store summaries arrive
  // hard-wrapped with embedded newlines (they are authored in the ERP's
  // textarea), and those reached the meta tag verbatim — counted against the
  // budget, and rendered into the attribute as literal line breaks.
  const trimmed = text.replace(/\s+/g, " ").trim();
  const { budget, floor } = getSnippetLimits(trimmed);

  if (trimmed.length <= budget) return trimmed;

  // Include Latin (.!?) and CJK (。！？) sentence terminators.
  const sentences = trimmed.match(/[^.!?。！？]+[.!?。！？]*\s*/g) ?? [trimmed];
  let out = "";
  for (const sentence of sentences) {
    const next = out + sentence;
    if (out.trimEnd().length >= floor && next.trimEnd().length > budget) break;
    out = next;
  }
  return out.trim() || sentences[0].trim();
}

const TITLE_BUDGET = 60;
const TITLE_BUDGET_CJK = 40;

/**
 * A page title with its brand suffix, kept inside the SERP budget where that
 * is possible without rewriting the headline.
 *
 * The blog is the case this exists for: article <title>s were `${post.title} |
 * Aqua MAG`, and 19 of 21 English ones ran past 60 characters — the longest at
 * 106 — so Google cut them, usually losing the brand segment it was cut to
 * carry. Dropping the suffix ourselves when the headline alone already fills
 * the budget is lossless: the same characters Google would have discarded, but
 * discarded in an order we choose.
 *
 * A headline that is over budget on its own is returned unchanged. Truncating
 * it would need an ellipsis mid-phrase, and shortening it is copy work, not a
 * formatting decision — same rule snippetDescription() follows for sentences.
 * Callers that want those shortened have to write shorter headlines.
 */
export function snippetTitle(headline: string, suffix = ""): string {
  const base = headline.replace(/\s+/g, " ").trim();
  // Same script detection the description budgets use, so a locale cannot be
  // CJK for one tag and Latin for the other.
  const budget = SCRIPT_BUDGETS[0].test.test(base) ? TITLE_BUDGET_CJK : TITLE_BUDGET;
  if (!suffix) return base;
  const withSuffix = `${base}${suffix}`;
  return withSuffix.length <= budget ? withSuffix : base;
}

/**
 * Last resort for a page with no per-locale copy: the English strings, marked
 * as belonging to VARS.
 *
 * Only reachable now for the noindex pages left in GENERIC_SEO, but the brand
 * suffix is still worth getting right, because appending it unconditionally
 * produced titles like "Aquaculture FAQs | VARS Aquaculture | VARS
 * Aquaculture" — every generic title already ends in a brand segment, so the
 * suffix duplicated it and ate ~24 characters of a ~60-character budget.
 */
function localizedGeneric(page: SeoPage, lang: LangCode): PageMeta {
  const base = GENERIC_SEO[page] ?? getSeoMeta("home");
  const alreadyBranded = /\|\s*VARS\b/i.test(base.title);
  const suffix = lang === "en" || alreadyBranded ? "" : " | VARS Aquaculture";
  return { title: `${base.title}${suffix}`, description: base.description };
}

function getCurrentLocaleForSeo(): LangCode {
  if (typeof window !== "undefined") {
    const first = window.location.pathname.split("/").filter(Boolean)[0]?.toLowerCase();
    if (first && VALID_LANGS.includes(first as LangCode)) return first as LangCode;
  }
  const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
    { getStore: () => { lang?: string } | undefined } | undefined;
  const lang = storage?.getStore()?.lang;
  return lang && VALID_LANGS.includes(lang as LangCode) ? (lang as LangCode) : "en";
}

const SEO_TRANSLATIONS: Record<string, Record<LangCode, PageMeta>> = {
  home: {
    en: {
      title: "VARS Aquaculture — Premium Aquaculture & Seafood Portal",
      description:
        "Certified salmon eggs, artemia, chlorella, feed additives and Mediterranean seafood — shipped worldwide from Türkiye.",
    },
    tr: {
      title: "VARS Su Ürünleri | Canlı Yem ve İhracat Çözümleri",
      description:
        "Canlı yem, somon yumurtası, mikroalg, yem girdileri ve Akdeniz su ürünleri için Türkiye merkezli B2B tedarik ve ihracat çözümlerini keşfedin.",
    },
    de: {
      title: "VARS Aquaculture — Aquakultur- & Meeresfrüchteportal",
      description:
        "Zertifizierte Lachseier, Artemia, Chlorella, Futtermittelzusatzstoffe und Meeresfrüchte aus dem Mittelmeer — weltweit abgewickelt aus der Türkei.",
    },
    ar: {
      title: "VARS Aquaculture — بوابة الاستزراع والمأكولات البحرية",
      description:
        "بيض السلمون المعتمد، والأرتيميا، والكلوريلا، وإضافات الأعلاف، والمأكولات البحرية المتوسطية — يتم شحنها عالميًا من تركيا.",
    },
    ru: {
      title: "VARS Aquaculture — Портал аквакультуры и морепродуктов",
      description:
        "Сертифицированная икра лосося, артемия, хлорелла, кормовые добавки и средиземноморские морепродукты — доставка по всему миру из Турции.",
    },
    ja: {
      title: "VARS Aquaculture — プレミアム養殖・水産物ポータル",
      description:
        "認定サーモン卵、アルテミア、クロレラ、飼料添加物, 地中海産水産物 — トルコから世界中へ配送されます。",
    },
    ko: {
      title: "VARS Aquaculture — 프리미엄 양식 및 수산물 포털",
      description:
        "인증된 연어 알, 알테미아, 클로렐라, 사료 첨가제 및 지중해 수산물 — 터키에서 전 세계로 발송됩니다.",
    },
    zh: {
      title: "VARS Aquaculture — 优质水产养殖与海鲜出口门户",
      description:
        "经认证的鲑鱼发眼卵、丰年虫卵、小球藻、饲料添加物及地中海新鲜海鲜出口 — 从土耳其销往全球。",
    },
    es: {
      title: "VARS Aquaculture — Portal premium de acuicultura y marisco",
      description:
        "Ovas de salmón certificadas, artemia, chlorella, aditivos para piensos y marisco mediterráneo — exportación global desde Turquía.",
    },
    fr: {
      title: "VARS Aquaculture — portail aquaculture et produits de la mer",
      description:
        "Œufs de saumon certifiés, artémia, chlorelle, additifs alimentaires et produits de la mer de Méditerranée — expédiés dans le monde entier depuis la Türkiye.",
    },
    fa: {
      title: "VARS Aquaculture — پورتال ممتاز آبزی‌پروری و آبزیان",
      description:
        "تخم ماهی آزاد گواهی‌شده، آرتمیا، کلرلا، افزودنی‌های خوراک و آبزیان مدیترانه‌ای — ارسال به سراسر جهان از ترکیه.",
    },
  },
  about: {
    en: {
      title: "About Us | VARS Aquaculture",
      description:
        "Learn about VARS Aquaculture's heritage in hatcheries sourcing, biosecurity protocols, and global client supply chain.",
    },
    tr: {
      title: "Hakkımızda | VARS Su Ürünleri",
      description:
        "VARS Su Ürünleri'nin kuluçkahane girdileri, biyogüvenlik uygulamaları, su ürünleri tedariki ve ihracat çalışmalarını tanıyın.",
    },
    de: {
      title: "Über uns | VARS Aquaculture",
      description:
        "Erfahren Sie mehr über das Erbe von VARS Aquaculture bei der Belieferung von Brutanlagen, Biosicherheitsprotokollen und der globalen Lieferkette.",
    },
    ar: {
      title: "من نحن | VARS Aquaculture",
      description:
        "تعرف على تاريخ VARS Aquaculture في توريد المفرخات، وبروتوكولات الأمن الحيوي، وسلسلة توريد العملاء العالمية.",
    },
    ru: {
      title: "О нас | VARS Aquaculture",
      description:
        "Узнайте об истории VARS Aquaculture в области снабжения инкубаторов, протоколах биобезопасности и глобальной цепочке поставок.",
    },
    ja: {
      title: "会社概要 | VARS Aquaculture",
      description:
        "孵化場への供給、バイオセキュリティプロトコル、グローバルなサプライチェーンにおけるVARS Aquacultureの実績について詳しく紹介します。",
    },
    ko: {
      title: "회사 소개 | VARS Aquaculture",
      description:
        "부화장 공급, 바이오보안 프로토콜, 그리고 글로벌 고객 공급망에서 VARS Aquaculture의 발자취에 대해 알아보세요.",
    },
    zh: {
      title: "关于我们 | VARS Aquaculture",
      description:
        "了解 VARS Aquaculture 在孵化场供给、生物安全协议以及全球客户供应链方面的历史累积。",
    },
    es: {
      title: "Quiénes somos | VARS Aquaculture",
      description:
        "Conozca la experiencia de VARS Aquaculture en el suministro de criaderos, protocolos de bioseguridad y cadena de suministro global.",
    },
    fr: {
      title: "À propos | VARS Aquaculture",
      description:
        "Découvrez l'expérience de VARS Aquaculture dans le sourcing auprès des écloseries, les protocoles de biosécurité et la chaîne d'approvisionnement de ses clients internationaux.",
    },
    fa: {
      title: "درباره ما | VARS Aquaculture",
      description:
        "با پیشینه VARS Aquaculture در تأمین از هچری‌ها، پروتکل‌های ایمنی زیستی و زنجیره تأمین مشتریان جهانی آشنا شوید.",
    },
  },
  products: {
    en: {
      title: "Product Catalog | VARS Aquaculture",
      description:
        "Explore our certified range of live feed, salmon eyed eggs, microalgae, feed additives, and fresh Mediterranean fish export.",
    },
    tr: {
      title: "Su Ürünleri Kataloğu | VARS",
      description:
        "Canlı yem, somon ve alabalık yumurtaları, mikroalgler, yem girdileri ve Akdeniz su ürünlerinden oluşan B2B ürün kataloğunu inceleyin.",
    },
    de: {
      title: "Produktkatalog | VARS Aquaculture",
      description:
        "Entdecken Sie unser zertifiziertes Angebot an Lebendfutter, Lachseiern, Mikroalgen, Futtermittelzusatzstoffen und frischem Fisch aus dem Mittelmeer.",
    },
    ar: {
      title: "كتالوج المنتجات | VARS Aquaculture",
      description:
        "استكشف مجموعتنا المعتمدة من الأعلاف الحية، وبيض السلمون، والطحالب الدقيقة، وإضافات الأعلاف، وتصدير الأسماك المتوسطية الطازجة.",
    },
    ru: {
      title: "Каталог продукции | VARS Aquaculture",
      description:
        "Изучите наш сертифицированный ассортимент живых кормов, икры лосося, микроводорослей, кормовых добавках и экспорте свежей рыбы.",
    },
    ja: {
      title: "製品カタログ | VARS Aquaculture",
      description:
        "認定生餌、サーモン卵、微細藻類、飼料添加物、新鮮な地中海産魚類輸出など、実績ある製品ラインナップをご覧ください。",
    },
    ko: {
      title: "제품 카탈로그 | VARS Aquaculture",
      description:
        "생알테미아, 연어 알, 미세조류, 사료 첨가제 및 신선한 지중해 어류 수출 등 당사의 인증된 제품 포트폴리오를 확인해 보세요.",
    },
    zh: {
      title: "产品目录 | VARS Aquaculture",
      description:
        "探索我们经认证的鲜活饵料、鲑鱼发眼卵、微藻、饲料添加剂和地中海新鲜鱼类出口产品系列。",
    },
    es: {
      title: "Catálogo de productos | VARS Aquaculture",
      description:
        "Explore nuestra gama certificada de alimento vivo, ovas de salmón con ojos, microalgas, aditivos para piensos y pescado fresco del Mediterráneo.",
    },
    fr: {
      title: "Catalogue produits | VARS Aquaculture",
      description:
        "Découvrez notre gamme certifiée de proies vivantes, d'œufs de saumon embryonnés, de microalgues, d'additifs alimentaires et de poissons frais de Méditerranée à l'export.",
    },
    fa: {
      title: "کاتالوگ محصولات | VARS Aquaculture",
      description:
        "مجموعه گواهی‌شده غذای زنده، تخم چشم‌زده ماهی آزاد، ریزجلبک، افزودنی‌های خوراک و صادرات ماهی تازه مدیترانه‌ای را ببینید.",
    },
  },
  blog: {
    en: {
      title: "Aqua MAG — Technical Journal & Articles | VARS",
      description:
        "Authoritative reviews on hatchery engineering, live feed nutrition, rotifer culture, and aquaculture biosecurity.",
    },
    tr: {
      title: "Aqua MAG | Su Ürünleri Teknik Makaleleri",
      description:
        "Kuluçkahane uygulamaları, canlı yem, rotifer kültürü, su ürünleri yetiştiriciliği ve biyogüvenlik üzerine teknik makaleleri okuyun.",
    },
    de: {
      title: "Aqua MAG — Technische Fachzeitschrift & Artikel | VARS",
      description:
        "Autoritative Berichte über Bruthaustechnik, Lebendfutternährstoffe, Rädertierchenzucht und Aquakultur-Biosicherheit.",
    },
    ar: {
      title: "Aqua MAG — المجلة التقنية والمقالات | VARS",
      description:
        "مراجعات موثوقة حول هندسة المفرخات، وتغذية الأعلاف الحية، واستزراع الروتيفير، والأمن الحيوي للاستزراع المائي.",
    },
    ru: {
      title: "Aqua MAG — Технический журнал и статьи | VARS",
      description:
        "Авторитетные обзоры по инкубационному инжинирингу, питанию живых кормов, культивированию коловраток и биобезопасности.",
    },
    ja: {
      title: "Aqua MAG — 技術ジャーナル＆記事 | VARS",
      description:
        "孵化場エンジニアリング、生餌의 영양、ワムシ培養、養殖バイオセキュリティに関する信頼のおける解説記事。",
    },
    ko: {
      title: "Aqua MAG — 기술 저널 및 기사 | VARS",
      description:
        "부화장 엔지니어링, 생알테미아 영양, 로티퍼 배양 및 양식 바이오보안에 관한 신뢰도 높은 기술 저널 자료집.",
    },
    zh: {
      title: "Aqua MAG — 技术期刊与文章 | VARS",
      description: "关于育苗工程、鲜活饵料营养、轮虫培养和水产养殖生物安全的权威文章技术期刊。",
    },
    es: {
      title: "Aqua MAG — Revista técnica y artículos | VARS",
      description:
        "Revisiones autorizadas sobre ingeniería de criaderos, nutrición de alimento vivo, cultivo de rotíferos y bioseguridad en acuicultura.",
    },
    fr: {
      title: "Aqua MAG — revue technique et articles | VARS",
      description:
        "Analyses de référence sur l'ingénierie d'écloserie, la nutrition par proies vivantes, la culture de rotifères et la biosécurité en aquaculture.",
    },
    fa: {
      title: "Aqua MAG — نشریه فنی و مقالات | VARS",
      description:
        "بررسی‌های تخصصی درباره مهندسی هچری، تغذیه با غذای زنده، پرورش روتیفر و ایمنی زیستی آبزی‌پروری.",
    },
  },
};

export function getSeoMeta(page: "home" | "about" | "products" | "blog"): PageMeta {
  let lang: LangCode = "en";
  if (typeof window !== "undefined") {
    const parts = window.location.pathname.split("/").filter(Boolean);
    if (parts.length > 0) {
      const first = parts[0].toLowerCase();
      if (VALID_LANGS.includes(first as LangCode)) {
        lang = first as LangCode;
      }
    }
  } else {
    const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
      { getStore: () => { lang?: string } | undefined } | undefined;
    const store = storage?.getStore();
    if (store?.lang) {
      lang = store.lang as LangCode;
    }
  }
  return SEO_TRANSLATIONS[page]?.[lang] || SEO_TRANSLATIONS[page]["en"];
}
